import { useState, useEffect, useCallback } from 'react';
import {
    getAllPendingAudits,
    getPendingAuditsByStatus,
    updateAuditSyncStatus,
    deleteAudit,
    getSyncQueue,
    removeFromSyncQueue,
    incrementRetryCount,
    countPendingAudits
} from '@/lib/storage';
import { api } from '@/lib/api-client';
import type { PendingAudit } from '@/types';

interface UseSyncReturn {
    pendingCount: number;
    isSyncing: boolean;
    lastSyncAt: Date | null;
    syncError: string | null;
    syncNow: () => Promise<void>;
    getPending: () => Promise<PendingAudit[]>;
}

const MAX_RETRIES = 3;

/**
 * Hook para sincronização de dados offline
 */
export function useSync(): UseSyncReturn {
    const [pendingCount, setPendingCount] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
    const [syncError, setSyncError] = useState<string | null>(null);

    // Atualizar contagem de pendentes
    const updatePendingCount = useCallback(async () => {
        const count = await countPendingAudits();
        setPendingCount(count);
    }, []);

    // Obter auditorias pendentes
    const getPending = useCallback(async (): Promise<PendingAudit[]> => {
        return getAllPendingAudits();
    }, []);

    // Sincronizar uma auditoria
    const syncAudit = useCallback(async (audit: PendingAudit): Promise<boolean> => {
        try {
            // Marcar como syncing
            await updateAuditSyncStatus(audit.id, 'syncing');

            // Criar auditoria no servidor
            const response = await api.post('/audits', {
                storeId: audit.storeId,
                notes: audit.notes,
            });

            if (!response.success) {
                throw new Error(response.error || 'Erro ao criar auditoria');
            }

            // TODO: Enviar measurements e backlight
            // Por enquanto, apenas remove a auditoria local

            // Remover do storage local
            await deleteAudit(audit.id);

            return true;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro desconhecido';
            await updateAuditSyncStatus(audit.id, 'error', message);
            return false;
        }
    }, []);

    // Sincronizar todos os dados pendentes
    const syncNow = useCallback(async () => {
        if (isSyncing) return;
        if (!navigator.onLine) {
            setSyncError('Sem conexão com a internet');
            return;
        }

        setIsSyncing(true);
        setSyncError(null);

        try {
            // Sincronizar auditorias pendentes
            const pendingAudits = await getPendingAuditsByStatus('pending');

            for (const audit of pendingAudits) {
                await syncAudit(audit);
            }

            // Sincronizar fila de sync
            const syncQueue = await getSyncQueue();

            for (const item of syncQueue) {
                if (item.retryCount >= MAX_RETRIES) {
                    // Muitos retries, remover da fila
                    await removeFromSyncQueue(item.id);
                    continue;
                }

                try {
                    // TODO: Processar item de acordo com o tipo
                    await removeFromSyncQueue(item.id);
                } catch {
                    await incrementRetryCount(item.id);
                }
            }

            setLastSyncAt(new Date());
            await updatePendingCount();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro na sincronização';
            setSyncError(message);
        } finally {
            setIsSyncing(false);
        }
    }, [isSyncing, syncAudit, updatePendingCount]);

    // Carregar contagem inicial
    useEffect(() => {
        updatePendingCount();
    }, [updatePendingCount]);

    // Sincronizar quando voltar online
    useEffect(() => {
        const handleOnline = () => {
            syncNow();
        };

        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, [syncNow]);

    // Sincronizar periodicamente (a cada 5 minutos)
    useEffect(() => {
        const interval = setInterval(() => {
            if (navigator.onLine && pendingCount > 0) {
                syncNow();
            }
        }, 5 * 60 * 1000);

        return () => clearInterval(interval);
    }, [syncNow, pendingCount]);

    return {
        pendingCount,
        isSyncing,
        lastSyncAt,
        syncError,
        syncNow,
        getPending,
    };
}

export default useSync;
